<template>
  <template v-if="isAuthenticated">
    <Drawer
      v-model:visible="drawerVisible"
      position="right"
      :header="'Notifications'"
      class="admin-inbox-drawer w-full max-w-lg"
    >
      <div
        class="flex flex-col gap-4 h-full overflow-y-auto pr-1 pb-1"
        @scroll.passive="onScroll"
      >
        <template v-if="flatItems.length">
          <div
            v-for="item in flatItems"
            :key="item.id"
            :class="[
              'admin-inbox-card rounded-xl border p-4 shadow-sm transition-colors',
              'border-surface-200 bg-surface-0',
            ]"
          >
            <div
              v-if="item.type === ADMIN_NOTIFICATION_TYPE_UP_AND_COMING"
              class="flex gap-4"
            >
              <div
                class="shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-surface-100 ring-1 ring-surface-200/80 flex items-center justify-center"
              >
                <img
                  v-if="upAndComingThumb(item.payload)"
                  :src="upAndComingThumb(item.payload)!"
                  class="w-full h-full object-cover"
                  alt=""
                  loading="lazy"
                />
                <i
                  v-else
                  class="fa-solid fa-gamepad text-surface-400 text-2xl"
                  aria-hidden="true"
                />
              </div>
              <div class="min-w-0 flex-1 flex flex-col gap-1">
                <div class="flex items-center justify-between gap-2">
                  <p
                    class="text-[11px] font-bold uppercase tracking-wider text-surface-500"
                  >
                    Up-and-coming
                  </p>
                  <div class="flex shrink-0 items-center gap-2">
                    <p class="flex items-center gap-2 text-sm text-surface-600">
                      {{ formatDate(item.createdAt) }}
                    </p>
                    <span
                      v-if="isInboxItemUnread(item)"
                      class="rounded-md bg-primary-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                    >
                      New
                    </span>
                  </div>
                </div>
                <div class="flex justify-between gap-16">
                  <p
                    class="text-base font-semibold leading-snug text-surface-900 sm:text-xl"
                  >
                    {{ upAndComingGameTitle(item.title) }}
                  </p>
                  <p
                    class="text-xl font-bold tabular-nums tracking-tight text-emerald-700"
                  >
                    {{ upAndComingGrowth(item.payload) }}
                  </p>
                </div>
                <div></div>
                <!-- <div
                  v-if="upAndComingGenre(item.payload)"
                  class="flex flex-col gap-1"
                >
                  <span
                    class="text-[11px] font-bold uppercase tracking-wider text-surface-500"
                  >
                    Genre
                  </span>
                  <p
                    class="flex items-start gap-2 text-sm font-medium leading-relaxed text-surface-800"
                  >
                    <i
                      class="fa-solid fa-tag mt-0.5 shrink-0 text-primary-500"
                      aria-hidden="true"
                    />
                    {{ upAndComingGenre(item.payload) }}
                  </p>
                </div> -->
                <!-- <div
                  v-for="g in upAndComingGrowthBlocks(item.payload)"
                  :key="g.label"
                  class="flex justify-between rounded-lg bg-surface-50 p-2 ring-1 ring-surface-100"
                >
                  <p class="text-xs font-medium text-surface-500">
                    {{ g.label }}
                  </p>
                  
                </div> -->
                <Button
                  v-if="payloadUniverseId(item.payload)"
                  :as="NuxtLink"
                  size="small"
                  severity="secondary"
                  class="font-bold"
                  :to="`/admin/games/${payloadUniverseId(item.payload)}/analytics`"
                  @click="drawerVisible = false"
                >
                  <i
                    class="fa-solid fa-chart-simple text-sm"
                    aria-hidden="true"
                  />
                  SHOW ANALYTICS
                </Button>
              </div>
            </div>
            <template v-else>
              <div class="flex items-start justify-between gap-2">
                <p
                  class="text-base font-semibold leading-snug text-surface-900"
                >
                  {{ item.title }}
                </p>
                <span
                  v-if="isInboxItemUnread(item)"
                  class="shrink-0 rounded-md bg-primary-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                >
                  New
                </span>
              </div>
              <p class="mt-2 text-sm text-surface-600">
                {{ formatDate(item.createdAt) }}
              </p>
            </template>
          </div>
        </template>
        <div
          v-else-if="!listFetching && drawerVisible"
          class="text-base text-surface-600"
        >
          No notifications.
        </div>
        <div v-if="listFetching" class="text-base text-surface-600">
          Loading…
        </div>
      </div>
    </Drawer>
  </template>
</template>

<script setup lang="ts">
  import Drawer from "primevue/drawer";
  import { useAdminInboxList } from "@admin-panel/composables/useAdminInbox";
  import {
    ADMIN_NOTIFICATION_TYPE_UP_AND_COMING,
    type UpAndComingNotificationPayload,
  } from "~/layers/general/inbox/notification-types";
  import Button from "primevue/button";
  import NuxtLink from "#app/components/nuxt-link";

  const auth = useAuth();
  const isAuthenticated = computed(() => auth.status.value === "authenticated");

  const drawerVisible = useState("adminInboxDrawerOpen", () => false);

  const { listInfiniteQuery, markAllReadMutation } =
    useAdminInboxList(drawerVisible);

  /** Snapshot of server `inboxLastReadAt` from the first list response in this open session. */
  const inboxReadBaseline = ref<Date | null>(null);

  const flatItems = computed(() => {
    const pages = listInfiniteQuery.data.value?.pages;
    if (!pages?.length) return [];
    return pages.flatMap((p) => p.items);
  });

  const listFetching = computed(
    () =>
      listInfiniteQuery.isFetching.value &&
      !listInfiniteQuery.data.value?.pages?.length,
  );

  function payloadUniverseId(payload: unknown): number | null {
    if (
      payload &&
      typeof payload === "object" &&
      "universeId" in payload &&
      typeof (payload as { universeId: unknown }).universeId === "number"
    ) {
      return (payload as { universeId: number }).universeId;
    }
    return null;
  }

  function parseUpAndComingPayload(
    payload: unknown,
  ): UpAndComingNotificationPayload | null {
    const universeId = payloadUniverseId(payload);
    if (universeId == null) return null;
    if (!payload || typeof payload !== "object") {
      return { universeId };
    }
    const p = payload as Record<string, unknown>;
    return {
      universeId,
      name: typeof p.name === "string" ? p.name : undefined,
      visitsChangeRateLast7d:
        typeof p.visitsChangeRateLast7d === "number"
          ? p.visitsChangeRateLast7d
          : undefined,
      iconUrl: typeof p.iconUrl === "string" ? p.iconUrl : null,
      displayGenre: typeof p.displayGenre === "string" ? p.displayGenre : null,
    };
  }

  function upAndComingThumb(payload: unknown): string | null {
    const p = parseUpAndComingPayload(payload);
    const url = p?.iconUrl;
    return url && url.length > 0 ? url : null;
  }

  function upAndComingGenre(payload: unknown): string | null {
    const g = parseUpAndComingPayload(payload)?.displayGenre;
    return g && g.length > 0 ? g : null;
  }

  /** Same convention as GamesTable "New Visits 7d": rate is 0–1 fraction. */
  function upAndComingGrowth(payload: unknown): string | null {
    const rate = parseUpAndComingPayload(payload)?.visitsChangeRateLast7d;
    if (rate == null || Number.isNaN(rate)) return null;
    const pct = rate * 100;
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct.toFixed(1)}%`;
  }

  /** Title already includes "Up-and-coming:" — show game name only next to the badge. */
  function upAndComingGameTitle(title: string): string {
    return title.replace(/^Up-and-coming:\s*/i, "").trim() || title;
  }

  function formatDate(d: Date | string) {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleString();
  }

  function notificationTime(createdAt: Date | string): Date {
    return typeof createdAt === "string" ? new Date(createdAt) : createdAt;
  }

  /** Matches server unread rule: createdAt &gt; inboxLastReadAt (strict). */
  function isInboxItemUnread(item: { createdAt: Date | string }): boolean {
    const baseline = inboxReadBaseline.value;
    if (!baseline) return false;
    return notificationTime(item.createdAt) > baseline;
  }

  watch(drawerVisible, async (open) => {
    if (!open) {
      inboxReadBaseline.value = null;
      return;
    }
    inboxReadBaseline.value = null;
    try {
      await listInfiniteQuery.refetch();
      const first = listInfiniteQuery.data.value?.pages?.[0];
      const iso = first?.inboxLastReadAt;
      inboxReadBaseline.value = iso ? new Date(iso) : new Date(0);
      await markAllReadMutation.mutateAsync();
    } catch {
      inboxReadBaseline.value = null;
    }
  });

  async function onScroll(e: Event) {
    const el = e.target as HTMLElement;
    if (!el || listInfiniteQuery.isFetchingNextPage.value) return;
    if (!listInfiniteQuery.hasNextPage.value) return;
    const threshold = 80;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      await listInfiniteQuery.fetchNextPage();
    }
  }
</script>

<style lang="scss" scoped>
  .admin-inbox-drawer :deep(.p-drawer-content) {
    padding-top: 0.5rem;
  }

  .admin-inbox-card--unread :deep(.p-button) {
    border-color: color-mix(in srgb, var(--p-primary-500) 35%, transparent);
  }
</style>
